import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import { ShoppingCart, Minus, Plus, Truck, RotateCcw, Shield, Star, Heart, MessageCircle, Bell, Package, FileText, CheckCircle, ChevronDown } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

// Translations
const translations = {
  ro: {
    inStock: 'În Stoc',
    outOfStock: 'Stoc Epuizat',
    available: 'disponibile',
    addToCart: 'Adaugă în Coș',
    orderWhatsApp: 'Comandă pe WhatsApp',
    addToFavorites: 'Adaugă la Favorite',
    removeFromFavorites: 'Șterge din Favorite',
    productCode: 'Cod Produs',
    selectSize: 'Selectează Mărime',
    description: 'Descriere',
    technicalSpecs: 'Specificații Tehnice',
    delivery: 'Livrare',
    reviews: 'Recenzii',
    noReviews: 'Nicio recenzie încă',
    writeReview: 'Scrie o recenzie',
    relatedProducts: 'Produse Similare',
    // Trust badges below SKU
    fastDelivery: 'Livrare Rapidă',
    fastDeliveryDesc: 'Oriunde în țară',
    fastDeliveryDesc2: 'Retur simplu și rapid',
    fastDeliveryDesc3: 'În 14 zile lucrătoare',
    securePayment: 'Plată securizată',
    securePaymentDesc: 'Checkout criptat SSL',
    phoneOrder: 'Comandă telefonic',
    phoneNumber: '0732901128',
    // Quality badge
    qualityGuarantee: 'Calitate Garantată',
    qualityGuaranteeDesc: '100% clienți mulțumiți',
    otherColors: 'Alte Culori Disponibile',
    clickToZoom: 'Click pentru zoom',
    quantity: 'Cantitate',
    notifyWhenAvailable: 'Anunță-mă când revine în stoc',
    enterEmail: 'Introdu adresa ta de email',
    notify: 'Anunță-mă',
    notifySuccess: 'Te vom anunța când produsul revine în stoc!',
    home: 'Acasă',
    shop: 'Magazin',
    // Delivery tab content
    deliveryInfo: 'Informații Livrare',
    deliveryText1: 'Livrare gratuită pentru comenzi peste 349 lei',
    deliveryText2: 'Livrare în 1-3 zile lucrătoare',
    deliveryText3: 'Curier rapid în toată România',
    deliveryText4: 'Posibilitate de retur în 14 zile',
    // Review restrictions
    onlyVerifiedBuyers: 'Doar clienții care au achiziționat acest produs pot lăsa o recenzie.',
    loginToReview: 'Conectează-te pentru a lăsa o recenzie.',
  },
  en: {
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    available: 'available',
    addToCart: 'Add to Cart',
    orderWhatsApp: 'Order on WhatsApp',
    addToFavorites: 'Add to Favorites',
    removeFromFavorites: 'Remove from Favorites',
    productCode: 'Product Code',
    selectSize: 'Select Size',
    description: 'Description',
    technicalSpecs: 'Technical Specifications',
    delivery: 'Delivery',
    reviews: 'Reviews',
    noReviews: 'No reviews yet',
    writeReview: 'Write a review',
    relatedProducts: 'Related Products',
    // Trust badges below SKU
    fastDelivery: 'Fast Delivery',
    fastDeliveryDesc: 'Anywhere in the country',
    fastDeliveryDesc2: 'Simple and fast return',
    fastDeliveryDesc3: 'Within 14 business days',
    securePayment: 'Secure Payment',
    securePaymentDesc: 'SSL encrypted checkout',
    phoneOrder: 'Order by Phone',
    phoneNumber: '0732901128',
    // Quality badge
    qualityGuarantee: 'Guaranteed Quality',
    qualityGuaranteeDesc: '100% satisfied customers',
    otherColors: 'Other Available Colors',
    clickToZoom: 'Click to zoom',
    quantity: 'Quantity',
    notifyWhenAvailable: 'Notify me when back in stock',
    enterEmail: 'Enter your email address',
    notify: 'Notify me',
    notifySuccess: 'We will notify you when the product is back in stock!',
    home: 'Home',
    shop: 'Shop',
    // Delivery tab content
    deliveryInfo: 'Delivery Information',
    deliveryText1: 'Free delivery for orders over 349 lei',
    deliveryText2: 'Delivery in 1-3 business days',
    deliveryText3: 'Fast courier across Romania',
    deliveryText4: 'Return option within 14 days',
    // Review restrictions
    onlyVerifiedBuyers: 'Only customers who have purchased this product can leave a review.',
    loginToReview: 'Login to leave a review.',
  }
};

// Reusable content components for tabs and accordion
const SpecsContent = ({ product, getLocalizedText }) => {
  if (product.technical_specs) {
    return (
      <div 
        className="prose prose-sm max-w-none [&_table]:w-full [&_table]:border-collapse [&_th]:bg-primary/10 [&_th]:text-primary [&_th]:font-semibold [&_th]:px-4 [&_th]:py-3 [&_th]:border [&_th]:border-gray-200 [&_td]:px-4 [&_td]:py-3 [&_td]:border [&_td]:border-gray-200 [&_tr:nth-child(even)]:bg-primary/5"
        dangerouslySetInnerHTML={{ __html: getLocalizedText(product.technical_specs) }}
      />
    );
  }
  
  if (getLocalizedText(product.description).includes('<')) {
    return (
      <div 
        className="prose prose-sm max-w-none [&_table]:w-full [&_table]:border-collapse [&_th]:bg-primary/10 [&_th]:text-primary [&_th]:font-semibold [&_th]:px-4 [&_th]:py-3 [&_th]:border [&_th]:border-gray-200 [&_td]:px-4 [&_td]:py-3 [&_td]:border [&_td]:border-gray-200 [&_tr:nth-child(even)]:bg-primary/5"
        dangerouslySetInnerHTML={{ __html: getLocalizedText(product.description) }}
      />
    );
  }
  
  return (
    <div className="overflow-hidden rounded-lg border">
      {getLocalizedText(product.description).split('\n').filter(line => line.trim()).map((line, index) => {
        const separators = [':', '-', '–'];
        let key = '';
        let value = line;
        
        for (const sep of separators) {
          if (line.includes(sep)) {
            const parts = line.split(sep);
            if (parts.length >= 2 && parts[0].trim().length < 50) {
              key = parts[0].trim();
              value = parts.slice(1).join(sep).trim();
              break;
            }
          }
        }
        
        return (
          <div 
            key={index} 
            className={`grid grid-cols-1 md:grid-cols-3 ${index % 2 === 0 ? 'bg-primary/5' : 'bg-white'}`}
          >
            {key ? (
              <>
                <div className="px-4 py-3 font-semibold text-primary md:border-r">
                  {key}
                </div>
                <div className="px-4 py-3 md:col-span-2 text-muted-foreground">
                  {value}
                </div>
              </>
            ) : (
              <div className="px-4 py-3 md:col-span-3 text-muted-foreground">
                {line}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DescriptionContent = ({ product, getLocalizedText }) => {
  if (getLocalizedText(product.description).includes('<')) {
    return (
      <div 
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: getLocalizedText(product.description) }}
      />
    );
  }
  
  return (
    <p className="text-muted-foreground whitespace-pre-line">
      {getLocalizedText(product.description)}
    </p>
  );
};

const DeliveryContent = ({ t }) => (
  <div className="space-y-4">
    <h3 className="font-heading text-lg font-semibold text-[#1A1625]">{t.deliveryInfo}</h3>
    <div className="grid md:grid-cols-2 gap-3">
      <div className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50">
        <Truck className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
        <p className="font-medium text-[#1A1625] text-sm">{t.deliveryText1}</p>
      </div>
      <div className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50">
        <Package className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
        <p className="font-medium text-[#1A1625] text-sm">{t.deliveryText2}</p>
      </div>
      <div className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50">
        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
        <p className="font-medium text-[#1A1625] text-sm">{t.deliveryText3}</p>
      </div>
      <div className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50">
        <RotateCcw className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
        <p className="font-medium text-[#1A1625] text-sm">{t.deliveryText4}</p>
      </div>
    </div>
  </div>
);

const ReviewsContent = ({ reviews, avgRating, t }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <h3 className="font-heading text-lg font-semibold text-[#1A1625]">
        {t.reviews} ({reviews.length})
      </h3>
      {reviews.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`h-5 w-5 ${star <= Math.round(avgRating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
              />
            ))}
          </div>
          <span className="font-medium">{avgRating}</span>
        </div>
      )}
    </div>

    {reviews.length > 0 ? (
      <div className="space-y-6">
        {reviews.map((review) => (
          <div key={review.id} className="border-b pb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                {review.customer_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium">{review.customer_name}</p>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-muted-foreground">{review.comment}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-muted-foreground">{t.noReviews}</p>
    )}

    {/* Note about verified buyers */}
    <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
      <p className="text-sm text-muted-foreground">
        <FileText className="h-4 w-4 inline mr-2" />
        {t.onlyVerifiedBuyers}
      </p>
    </div>
  </div>
);

export const ProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { getLocalizedText, formatPrice, addToCart, API, language, settings } = useApp();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const [submittingNotify, setSubmittingNotify] = useState(false);

  const t = translations[language] || translations.ro;

  // Check favorites on load
  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem('rani_favorites') || '[]');
    setIsFavorite(favorites.includes(productId));
  }, [productId]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/products/${productId}`);
        if (!res.ok) throw new Error('Product not found');
        const data = await res.json();
        setProduct(data);

        // Fetch related products
        if (data.category_id || data.category_ids?.length > 0) {
          const catId = data.category_ids?.[0] || data.category_id;
          const relatedRes = await fetch(`${API}/products?category=${catId}&limit=4`);
          const relatedData = await relatedRes.json();
          setRelatedProducts((relatedData.products || []).filter(p => p.id !== productId));
        }

        // Fetch reviews
        const reviewsRes = await fetch(`${API}/products/${productId}/reviews`);
        const reviewsData = await reviewsRes.json();
        setReviews(reviewsData || []);
      } catch (err) {
        console.error('Failed to fetch product', err);
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    setQuantity(1);
    setSelectedImage(0);
    setShowLightbox(false);
    setSelectedSize('');
  }, [productId, API, navigate]);

  const handleAddToCart = () => {
    if (product.sizes?.length > 0 && !selectedSize) {
      toast.error(t.selectSize);
      return;
    }
    addToCart({ ...product, selectedSize }, quantity);
    toast.success(t.addToCart, {
      description: getLocalizedText(product.name),
    });
  };

  const handleWhatsAppOrder = () => {
    const phone = settings?.contact_phone?.replace(/\D/g, '') || '40700000000';
    const productName = getLocalizedText(product.name);
    const price = formatPrice(product.prices);
    const message = encodeURIComponent(
      `Buna ziua! As dori sa comand: ${productName} (${price})${selectedSize ? ` - Marime: ${selectedSize}` : ''} - Cantitate: ${quantity}`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('rani_favorites') || '[]');
    if (isFavorite) {
      const newFavorites = favorites.filter(id => id !== productId);
      localStorage.setItem('rani_favorites', JSON.stringify(newFavorites));
      setIsFavorite(false);
      toast.success(t.removeFromFavorites);
    } else {
      favorites.push(productId);
      localStorage.setItem('rani_favorites', JSON.stringify(favorites));
      setIsFavorite(true);
      toast.success(t.addToFavorites);
    }
    // Dispatch event to update header count
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (!notifyEmail || !notifyEmail.includes('@')) return;
    
    setSubmittingNotify(true);
    try {
      await fetch(`${API}/products/${productId}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: notifyEmail })
      });
      toast.success(t.notifySuccess);
      setShowNotifyForm(false);
      setNotifyEmail('');
    } catch (err) {
      toast.success(t.notifySuccess); // Show success anyway for UX
      setShowNotifyForm(false);
    } finally {
      setSubmittingNotify(false);
    }
  };

  // Get SEO meta data
  const getSeoData = () => {
    if (!product) return {};
    const productName = getLocalizedText(product.name);
    const productDesc = getLocalizedText(product.short_description || product.description);
    return {
      title: product.meta_title?.[language] || product.meta_title?.en || `${productName} | Rani Store`,
      description: product.meta_description?.[language] || product.meta_description?.en || productDesc?.substring(0, 160),
      keywords: product.meta_keywords?.[language] || product.meta_keywords?.en || product.tags?.join(', ') || '',
    };
  };

  const seoData = getSeoData();

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!product) return null;

  const images = product.images?.length > 0 
    ? product.images 
    : ['https://images.unsplash.com/photo-1763478959550-13d564c5a62d?q=80&w=800&auto=format&fit=crop'];

  const hasDiscount = product.discount_percent > 0;
  const originalPrice = formatPrice(product.prices);
  const discountedPrice = hasDiscount
    ? formatPrice({
        ron: product.prices.ron * (1 - product.discount_percent / 100),
        eur: product.prices.eur * (1 - product.discount_percent / 100),
      })
    : null;

  // Calculate average rating
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  // Get first image URL for SEO
  const getImageUrl = (img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    if (img.startsWith('/api')) return `${API.replace('/api', '')}${img}`;
    return img;
  };

  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: t.home, url: '/' },
    { name: t.shop, url: '/shop' },
    { name: getLocalizedText(product.name) },
  ];

  return (
    <Layout>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        image={getImageUrl(product?.images?.[0])}
        url={`/product/${product.id}`}
        type="product"
        language={language}
        product={{
          name: getLocalizedText(product.name),
          description: getLocalizedText(product.short_description || product.description)?.substring(0, 200),
          images: product.images?.map(getImageUrl),
          sku: product.sku,
          price: product.prices?.ron,
          stock: product.stock,
          rating: avgRating > 0 ? parseFloat(avgRating) : null,
          reviewCount: reviews.length,
        }}
        breadcrumbs={breadcrumbs}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8" data-testid="product-page">
        {/* Breadcrumb */}
        <nav className="text-sm mb-6">
          <ol className="flex items-center gap-2 text-muted-foreground">
            <li><Link to="/" className="hover:text-primary">{t.home}</Link></li>
            <li>/</li>
            <li><Link to="/shop" className="hover:text-primary">{t.shop}</Link></li>
            <li>/</li>
            <li className="text-foreground truncate max-w-[200px]">{getLocalizedText(product.name)}</li>
          </ol>
        </nav>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Images - eMAG Style Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            {/* Main Image */}
            <div className="relative aspect-square overflow-hidden rounded-lg bg-white border border-gray-200 group">
              <img
                src={images[selectedImage]}
                alt={getLocalizedText(product.name)}
                className="w-full h-full object-contain cursor-zoom-in transition-transform duration-300 group-hover:scale-110"
                data-testid="product-main-image"
                onClick={() => setShowLightbox(true)}
              />
              {hasDiscount && (
                <span className="absolute top-3 left-3 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded shadow-md z-10">
                  -{product.discount_percent}%
                </span>
              )}
              {product.sku && (
                <span className="absolute top-3 right-3 bg-gray-800/80 text-white text-xs font-mono px-2 py-1 rounded z-10">
                  {product.sku}
                </span>
              )}
              <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                {t.clickToZoom}
              </div>
            </div>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <div className="relative">
                {images.length > 5 && (
                  <button
                    onClick={() => {
                      const container = document.getElementById('thumbnail-strip');
                      if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
                    }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-1.5 hover:bg-gray-100 border"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                
                <div 
                  id="thumbnail-strip"
                  className="flex gap-2 overflow-x-auto px-8 py-2 scroll-smooth"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      onMouseEnter={() => setSelectedImage(i)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        selectedImage === i 
                          ? 'border-primary shadow-md scale-105' 
                          : 'border-gray-200 hover:border-primary/50'
                      }`}
                    >
                      <img src={img} alt={`${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>

                {images.length > 5 && (
                  <button
                    onClick={() => {
                      const container = document.getElementById('thumbnail-strip');
                      if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
                    }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-1.5 hover:bg-gray-100 border"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </motion.div>

          {/* Lightbox */}
          {showLightbox && (
            <div 
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
              onClick={() => setShowLightbox(false)}
            >
              <button className="absolute top-4 right-4 text-white hover:text-gray-300 z-50" onClick={() => setShowLightbox(false)}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev === 0 ? images.length - 1 : prev - 1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 rounded-full p-3"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <img src={images[selectedImage]} alt="" className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
              
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev === images.length - 1 ? 0 : prev + 1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 rounded-full p-3"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
                {selectedImage + 1} / {images.length}
              </div>
            </div>
          )}

          {/* Product Details - Right Side */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-6"
          >
            {/* Title */}
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1625]" data-testid="product-title">
              {getLocalizedText(product.name)}
            </h1>

            {/* Short Description - Support HTML */}
            {product.short_description && (
              getLocalizedText(product.short_description).includes('<') ? (
                <div 
                  className="text-muted-foreground prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: getLocalizedText(product.short_description) }}
                />
              ) : (
                <p className="text-muted-foreground whitespace-pre-line">
                  {getLocalizedText(product.short_description)}
                </p>
              )
            )}

            {/* Price */}
            <div className="flex items-center gap-4">
              {hasDiscount ? (
                <>
                  <span className="text-3xl font-bold text-primary" data-testid="product-price">{discountedPrice}</span>
                  <span className="text-xl text-muted-foreground line-through">{originalPrice}</span>
                </>
              ) : (
                <span className="text-3xl font-bold text-foreground" data-testid="product-price">{originalPrice}</span>
              )}
            </div>

            {/* Stock Status - Show only yes/no, no numbers */}
            <div className="space-y-3">
              {product.stock > 0 ? (
                <span className="inline-flex items-center text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full font-medium">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t.inStock}
                </span>
              ) : (
                <div className="space-y-3">
                  <span className="inline-flex items-center text-sm text-red-600 bg-red-50 px-3 py-1 rounded-full font-medium">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    {t.outOfStock}
                  </span>
                  
                  {/* Notify when back in stock */}
                  {!showNotifyForm ? (
                    <button
                      onClick={() => setShowNotifyForm(true)}
                      className="flex items-center gap-2 text-primary hover:text-purple-600 text-sm font-medium"
                    >
                      <Bell className="h-4 w-4" />
                      {t.notifyWhenAvailable}
                    </button>
                  ) : (
                    <form onSubmit={handleNotifySubmit} className="flex gap-2">
                      <Input
                        type="email"
                        placeholder={t.enterEmail}
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        className="flex-1"
                        required
                      />
                      <Button 
                        type="submit" 
                        disabled={submittingNotify}
                        className="bg-primary hover:bg-purple-600"
                      >
                        {submittingNotify ? '...' : t.notify}
                      </Button>
                    </form>
                  )}
                </div>
              )}
            </div>

            {/* Sizes - Only show if product has sizes */}
            {product.sizes?.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">{t.selectSize}</label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 border-2 rounded-lg font-medium transition-all ${
                        selectedSize === size
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-200 hover:border-primary'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Variants from Images - when images_as_colors is enabled */}
            {product.images_as_colors && images.length > 1 && (
              <div>
                <label className="block text-sm font-medium mb-2">{t.otherColors}</label>
                <div className="flex flex-wrap gap-2">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === i
                          ? 'border-primary ring-2 ring-primary/30 shadow-md scale-105'
                          : 'border-gray-200 hover:border-primary/50'
                      }`}
                    >
                      <img src={img} alt={`Color ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Variants - linked to other products */}
            {product.colors?.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">{t.otherColors}</label>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color, i) => (
                    <Link
                      key={i}
                      to={color.product_id ? `/product/${color.product_id}` : '#'}
                      className="block w-12 h-12 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-primary transition-colors"
                    >
                      {color.image ? (
                        <img src={color.image} alt={getLocalizedText(color.name)} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xs">
                          {getLocalizedText(color.name)}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity & Actions */}
            <div className="space-y-4 pt-4 border-t">
              {/* Quantity Selector - Limit to 10 max without showing stock */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">Cantitate:</span>
                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 hover:bg-gray-100 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="px-4 font-medium w-12 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(10, quantity + 1))}
                    className="p-2 hover:bg-gray-100 transition-colors"
                    disabled={quantity >= 10}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                {/* Add to Cart - Purple */}
                <Button
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                  className="w-full bg-primary hover:bg-purple-600 text-white py-6 text-lg font-semibold rounded-xl"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  {t.addToCart}
                </Button>

                {/* Order on WhatsApp - Green */}
                <Button
                  onClick={handleWhatsAppOrder}
                  disabled={product.stock === 0}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg font-semibold rounded-xl"
                  data-testid="whatsapp-btn"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  {t.orderWhatsApp}
                </Button>

                {/* Add to Favorites */}
                <Button
                  onClick={toggleFavorite}
                  variant="outline"
                  className={`w-full py-5 rounded-xl ${isFavorite ? 'border-red-500 text-red-500' : ''}`}
                  data-testid="favorite-btn"
                >
                  <Heart className={`mr-2 h-5 w-5 ${isFavorite ? 'fill-red-500' : ''}`} />
                  {isFavorite ? t.removeFromFavorites : t.addToFavorites}
                </Button>
              </div>
            </div>

            {/* Product Code (SKU) */}
            {product.sku && (
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">{t.productCode}:</span> {product.sku}
              </div>
            )}

            {/* Trust Badges - Under Product Code - 4 badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t">
              {/* Fast Delivery */}
              <div className="text-center border rounded-lg p-3 bg-gray-50">
                <Truck className="h-5 w-5 mx-auto text-primary mb-2" />
                <p className="text-xs font-semibold text-[#1A1625]">{t.fastDelivery}</p>
                <p className="text-xs text-muted-foreground">{t.fastDeliveryDesc}</p>
              </div>
              {/* Returns */}
              <div className="text-center border rounded-lg p-3 bg-gray-50">
                <svg className="h-5 w-5 mx-auto text-primary mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 14l-4-4 4-4"/>
                  <path d="M5 10h11a4 4 0 1 1 0 8h-1"/>
                </svg>
                <p className="text-xs font-semibold text-[#1A1625]">{t.fastDeliveryDesc2}</p>
                <p className="text-xs text-muted-foreground">{t.fastDeliveryDesc3}</p>
              </div>
              {/* Secure Payment */}
              <div className="text-center border rounded-lg p-3 bg-gray-50">
                <Shield className="h-5 w-5 mx-auto text-primary mb-2" />
                <p className="text-xs font-semibold text-[#1A1625]">{t.securePayment}</p>
                <p className="text-xs text-muted-foreground">{t.securePaymentDesc}</p>
              </div>
              {/* Phone Order */}
              <div className="text-center border rounded-lg p-3 bg-gray-50">
                <MessageCircle className="h-5 w-5 mx-auto text-primary mb-2" />
                <p className="text-xs font-semibold text-[#1A1625]">{t.phoneOrder}</p>
                <p className="text-xs text-muted-foreground font-medium">{t.phoneNumber}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Product Tabs - Desktop: Tabs, Mobile: Accordion */}
        <div className="mt-12 border-t pt-8">
          {/* Desktop Tabs - Hidden on mobile */}
          <div className="hidden md:block">
            <Tabs defaultValue="specs" className="w-full">
              <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0 gap-0">
                <TabsTrigger 
                  value="specs" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3 font-medium text-sm"
                >
                  {t.technicalSpecs}
                </TabsTrigger>
                <TabsTrigger 
                  value="description" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3 font-medium text-sm"
                >
                  {t.description}
                </TabsTrigger>
                <TabsTrigger 
                  value="delivery" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3 font-medium text-sm"
                >
                  {t.delivery}
                </TabsTrigger>
                <TabsTrigger 
                  value="reviews" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3 font-medium text-sm"
                >
                  {t.reviews} ({reviews.length})
                </TabsTrigger>
              </TabsList>

              {/* Technical Specifications Tab */}
              <TabsContent value="specs" className="pt-6">
                <SpecsContent product={product} getLocalizedText={getLocalizedText} />
              </TabsContent>

              {/* Description Tab */}
              <TabsContent value="description" className="pt-6">
                <DescriptionContent product={product} getLocalizedText={getLocalizedText} />
              </TabsContent>

              {/* Delivery Tab */}
              <TabsContent value="delivery" className="pt-6">
                <DeliveryContent t={t} />
              </TabsContent>

              {/* Reviews Tab */}
              <TabsContent value="reviews" className="pt-6">
                <ReviewsContent reviews={reviews} avgRating={avgRating} t={t} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Mobile Accordion - Hidden on desktop */}
          <div className="md:hidden">
            <Accordion type="multiple" className="w-full">
              <AccordionItem value="specs" className="border rounded-lg mb-2 overflow-hidden">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50 font-medium">
                  {t.technicalSpecs}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <SpecsContent product={product} getLocalizedText={getLocalizedText} />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="description" className="border rounded-lg mb-2 overflow-hidden">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50 font-medium">
                  {t.description}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <DescriptionContent product={product} getLocalizedText={getLocalizedText} />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="delivery" className="border rounded-lg mb-2 overflow-hidden">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50 font-medium">
                  {t.delivery}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <DeliveryContent t={t} />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="reviews" className="border rounded-lg mb-2 overflow-hidden">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50 font-medium">
                  {t.reviews} ({reviews.length})
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <ReviewsContent reviews={reviews} avgRating={avgRating} t={t} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12 border-t pt-8">
            <h2 className="font-heading text-xl font-bold text-[#1A1625] mb-6">{t.relatedProducts}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductPage;
